import { AxiosResponse } from 'axios';
import requestClient from '../components/Utility/RequestClient';
import { Knowledgebase, KnowledgebaseDocumentStatus, KnowledgebaseFormData, KnowledgebaseSource } from '../Types';
import { StoreEnvironment } from '../Types/StoreTypes';
import { store } from '../store';

const resolveDemo = () => (store.getState().environment as StoreEnvironment).demoInstance;

export const knowledgebaseService = {
    getKnowledgebases: (): Promise<AxiosResponse<Knowledgebase[]>> => {
        return requestClient.get(`${resolveDemo() ? '/demo' : ''}/knowledgebases/get`);
    },

    getKnowledgebasesByPage: (
        page: number,
        itemsPerPage: number,
        optionalParams?: Record<string, any>,
        query?: string | null,
    ): Promise<{ data: { data: Knowledgebase[]; total: number } }> => {
        return requestClient
            .get(`${resolveDemo() ? '/demo' : ''}/knowledgebases/get`, {
                params: {
                    page,
                    itemsPerPage,
                    query,
                },
            })
            .then((response: any) => response);
    },

    getKnowledgebase: (id: string): Promise<AxiosResponse<Knowledgebase>> => {
        return requestClient.get(`${resolveDemo() ? '/demo' : ''}/knowledgebases/get/${id}`);
    },

    getKnowledgebaseDocuments: async (
        sourceId: string,
        status: KnowledgebaseDocumentStatus | null,
        page: number,
        itemsPerPage: number,
        query?: string | null,
    ): Promise<{ data: { data: Knowledgebase[]; total: number } }> => {
        return requestClient.get(`/knowledgebases/get/source/${sourceId}/documents`, {
            params: {
                status,
                page,
                itemsPerPage,
                query,
            },
        });
    },

    reembedDocument: (documentIdentifier: string): Promise<AxiosResponse<any>> => {
        return requestClient.post(`/knowledgebases/document/${documentIdentifier}/reembed`);
    },

    createKnowledgebase: (data: KnowledgebaseFormData): Promise<AxiosResponse<Knowledgebase>> => {
        const formData = new FormData();
        formData.append('name', data.name || '');
        formData.append('description', data.description || '');

        return requestClient.post('/knowledgebases/create', formData);
    },

    updateKnowledgebase: (id: string, data: KnowledgebaseFormData): Promise<AxiosResponse<Knowledgebase>> => {
        const formData = new FormData();
        formData.append('name', data.name || '');
        formData.append('description', data.description || '');

        return requestClient.post(`/knowledgebases/update/${id}`, formData);
    },

    deleteKnowledgebase: (id: string): Promise<AxiosResponse<void>> => {
        return requestClient.delete(`/knowledgebases/delete/${id}`);
    },

    uploadFile: async (formData: any): Promise<any> => {
        return requestClient.post(`/knowledgebases/uploadfile`, formData);
    },

    getSourceProcessors: () => {
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.get(`${base}/knowledgebaseSources/sources/default`);
    },

    getKnowledgebaseSources: (knowledgebaseIdentifier: string): Promise<AxiosResponse<any>> => {
        return requestClient.get(
            `${resolveDemo() ? '/demo' : ''}/knowledgebaseSources/${knowledgebaseIdentifier}/sources`,
        );
    },

    createKnowledgebaseSource: (source: KnowledgebaseSource, knowledgebaseIdentifier: string): Promise<any> => {
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.post(`${base}/knowledgebaseSources/${knowledgebaseIdentifier}/sources/create`, source);
    },

    deleteKnowledgebaseSource: (id: string): Promise<AxiosResponse<void>> => {
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.delete(`${base}/knowledgebaseSources/sources/delete/${id}`);
    },

    syncKnowledgebaseSourceNow: (sourceIdentifier: string): Promise<AxiosResponse<any>> => {
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.post(`${base}/knowledgebaseSources/source/${sourceIdentifier}/syncNow`);
    },

    sourceValidate: (source: KnowledgebaseSource) => {
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.post(`${base}/knowledgebaseSources/source/validate`, source);
    },

    saveDocumentsToSource: (files: any[], sourceIdentifier: string) => {
        const formData = new FormData();
        files.forEach((file) => {
            formData.append('documents[]', file.sha1);
        });
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.post(`${base}/knowledgebaseSources/source/${sourceIdentifier}/saveDocuments`, formData);
    },

    removeExistingDocument: (sourceIdentifier: string, documentIdentifier: string) => {
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.delete(
            `${base}/knowledgebaseSources/source/${sourceIdentifier}/document/${documentIdentifier}/remove`,
        );
    },

    getDocument: (documentIdentifier: string): Promise<AxiosResponse<any>> => {
        const base = resolveDemo() ? '/demo' : '';
        return requestClient.get(`${base}/knowledgebaseSources/document/${documentIdentifier}/download`, {
            responseType: 'blob',
        });
    },
};
